/**
 * M6.5-W6 — "Verplaats uit folder…" dialog.
 *
 * In Base-Foldertree per concept een 📤-knop. Klik → deze dialog opent
 * → folder-membership + «Reference Object»-stereotype worden gestript
 * en branch_id wisselt naar de **write-houder-WP** van de folder.
 *
 * Target is **niet** kiesbaar: het exclusieve-write-eigendom-principe
 * (max 1 WP per folder met mode='write') verbiedt dat objecten in een
 * andere WP belanden dan de write-houder. Wanneer geen WP write-recht
 * heeft op de folder is move-out geblokkeerd; admin moet eerst via
 * /settings → Workpackages → Folder-autorisaties een write-houder
 * toewijzen.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";

interface Props {
  workspaceId: string;
  folderId: string;
  conceptIds: readonly string[];
  /** `refresh=true` → caller moet refreshen na succes. */
  onClose: (refresh: boolean) => void;
}

export function MoveOutOfFolderDialog({ workspaceId, folderId, conceptIds, onClose }: Props) {
  const { t } = useTranslation();
  const [writeHolderName, setWriteHolderName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getFolderWriteHolder(workspaceId, folderId)
      .then((r) => {
        if (cancelled) return;
        setWriteHolderName(r.workpackageName);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, folderId]);

  const canSubmit = writeHolderName !== null && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.moveConceptsOutOfFolder(workspaceId, folderId, [...conceptIds]);
      if (res.skipped.length > 0) {
        setError(
          t("moveToFolder.partial", {
            moved: res.moved.length,
            skipped: res.skipped.length,
            reason: res.skipped[0]?.reason ?? t("moveToFolder.unknown"),
          }),
        );
        return;
      }
      onClose(true);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
      setError(t("moveOutOfFolder.failed", { msg }));
    } finally {
      setSubmitting(false);
    }
  };

  const node = (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationele overlay-laag; sluiten via Escape + knoppen, klik-buiten is muis-gemak
    <div
      className="ledger fixed inset-0 z-[100] flex items-center justify-center bg-black/30"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose(false);
      }}
      role="presentation"
    >
      <div
        className="w-96 max-w-[90vw] rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="border-(--ld-border) border-b px-4 py-2 font-semibold text-(--ld-text) text-sm">
          {t("moveOutOfFolder.title", { count: conceptIds.length })}
        </header>
        <div className="px-4 py-3 text-sm">
          <p className="mb-2 text-(--ld-muted) text-xs">{t("moveOutOfFolder.intro")}</p>
          {loading ? (
            <p className="text-(--ld-muted) italic">{t("moveOutOfFolder.loadingWriteHolder")}</p>
          ) : writeHolderName === null ? (
            <p className="rounded border border-transparent bg-(--ld-notice-bg) px-2 py-2 text-(--ld-notice-ink) text-xs">
              {t("moveOutOfFolder.noWriteHolder")}{" "}
              <span className="font-mono">{t("moveOutOfFolder.settingsPath")}</span>.
            </p>
          ) : (
            <div className="flex flex-col gap-1 text-xs">
              <span className="text-(--ld-muted)">{t("moveOutOfFolder.targetLabel")}</span>
              <div className="rounded border border-(--ld-control) bg-(--ld-raised) px-2 py-1.5 font-medium text-(--ld-text) text-sm">
                {writeHolderName}
              </div>
            </div>
          )}
          {error && (
            <p className="mt-3 rounded border border-(--ld-control) bg-(--ld-negative-bg) px-2 py-1 text-(--ld-negative-ink) text-xs">
              {error}
            </p>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-(--ld-border) border-t bg-(--ld-raised) px-3 py-2">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
          >
            {t("settings.common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={!canSubmit}
            className="rounded bg-(--ld-primary) px-3 py-1 text-(--ld-primary-ink) text-xs hover:bg-(--ld-primary-hover) disabled:opacity-50"
          >
            {t("moveToFolder.confirm")}
          </button>
        </footer>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
